export function observationTime(value) {
  if (typeof value !== 'string' || !value.trim()) return NaN;
  const s = value.trim();
  return Date.parse(/[zZ]$|[+-]\d{2}:?\d{2}$|(?:GMT|UTC)$/i.test(s) ? s : s + 'Z');
}
export function observationState(status, stamp, now = Date.now(), maxAge = 30 * 60000) {
  const t = observationTime(stamp);
  if (!Number.isFinite(t) || status === 'unavailable') return 'unavailable';
  if (status === 'stale' || now - t > maxAge || t - now > 5 * 60000) return 'stale';
  return status === 'limited' ? 'limited' : 'fresh';
}
export function ingestObservation(source, refreshFailed = false, now = Date.now()) {
  const maxAge = source.freshness_policy === 'static' ? Infinity : (source.max_age_hours || 9) * 3600000;
  return {
    status: observationState(refreshFailed || source.status !== 'ok' ? 'stale' : 'fresh', source.last_success_at, now, maxAge),
    stamp: source.last_success_at,
    static: source.freshness_policy === 'static',
    warning: source.warning === 'refresh_failed_using_retained_data',
  };
}
export function weatherDomain(weather, domain, now = Date.now()) {
  const w = weather?.wind;
  const value = domain === 'kp' ? weather?.kp?.current : domain === 'wind' ? w?.speed_kms_raw : w?.bz;
  const stamp = domain === 'kp' ? weather?.kp?.updated : domain === 'wind' ? w?.updated : w?.mag_updated;
  const status = weather?.refresh_failed ? 'stale' : domain === 'kp' ? weather?.status : 'fresh';
  return { status: Number.isFinite(value) ? observationState(status, stamp, now) : 'unavailable', stamp };
}
export function currentKp(weather, now = Date.now()) {
  return weatherDomain(weather, 'kp', now).status === 'fresh' ? weather.kp.current : null;
}
export function currentWind(weather, key, domain = 'wind', now = Date.now()) {
  return weatherDomain(weather, domain, now).status === 'fresh' && Number.isFinite(weather?.wind?.[key]) ? weather.wind[key] : null;
}
export function usableVerdict(v) {
  return v?.data_status === 'fresh' && Number.isFinite(v?.score) && v.score >= 0 && v.score <= 100;
}
export function observingPresentation(v) {
  if (v?.assessment && !usableVerdict(v)) {
    const status = v.data_status === 'stale' ? 'stale' : v.data_status === 'limited' ? 'limited' : 'unavailable';
    return {...v.assessment,status,headline:status === 'stale' ? 'Observations stale' : status === 'limited' ? 'Limited observations' : 'Observations unavailable',reason:'Incomplete or stale observations; no go-outside recommendation can be made.',ai_eligible:false};
  }
  return v?.assessment || { status: 'unavailable', headline: 'Observations unavailable', reason: 'Awaiting an assessment for the selected location and time.', ai_eligible: false, score_label: 'Weighted conditions score' };
}
const dateLabel = ms => Number.isFinite(ms) ? new Date(ms).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', timeZoneName:'short' }) : 'time unavailable';
export function observingWindow(v) {
  const a = observingPresentation(v);
  return `Sky at ${dateLabel(a.instant_at)} · weather to ${dateLabel(a.weather_end_at)} (12h avg)`;
}
export function passWindow(pass, label = 'Selected pass') {
  return `${label} · ${dateLabel(Date.parse(pass?.start))} – ${dateLabel(Date.parse(pass?.end))} · ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
}
export function passOptics(pass) {
  if (!Number.isFinite(pass?.observer_sun_el)) return 'Visibility unconfirmed: observer twilight is unavailable';
  if (pass.observer_sun_el > -6 || !pass.visibility_tier) return 'Not rated for naked-eye viewing in this pass window';
  const mag = pass.approx_magnitude;
  if (!Number.isFinite(mag)) return 'Brightness unconfirmed; visibility depends on clouds and local sky';
  return mag > 3 ? 'Faint, binoculars or a camera may help in this window' : 'Potential naked-eye pass in this window; check clouds and local sky';
}
